import { useCallback, useMemo, useState } from 'react'
import type { Creative, CreativeType, LinkedInPostContent } from '../types'
import { useFlushPersistNow, useUpdateCreativeContent } from '../store/draft-creative-context'
import { FieldGroup } from '../components/FieldGroup'
import { fieldLengthState, platformContentViolations } from '../utils/validatePlatformContent'
import { ImagePickerModal } from '../components/ImagePickerModal'
import { useEditorHighlight } from '../hooks/useEditorHighlight'
import { VideoPickerBody } from './fields/VideoPickerBody'
import { HashtagInput } from './fields/HashtagInput'
import { ImagePickerButton } from './fields/ImagePickerButton'

interface LinkedInPostEditorProps {
  creative: Creative<CreativeType.LinkedInPost>
}

export function LinkedInPostEditor({ creative }: LinkedInPostEditorProps) {
  const updateContent = useUpdateCreativeContent()
  const flushPersistNow = useFlushPersistNow()
  const { editorRef, highlightedSection, handleEditorBlur, setActiveSection } = useEditorHighlight(
    creative.id
  )
  const content = creative.content

  // LinkedIn's published limit: 3,000 characters for the text of a UGC post. The
  // counter measures the caption PLUS every hashtag, matching what
  // LinkedInPublishService sends as `commentary`.
  const violationByField = useMemo(() => {
    const map = new Map<string, string>()
    for (const v of platformContentViolations({ type: creative.type, content })) {
      if (!map.has(v.contentField)) map.set(v.contentField, v.message)
    }
    return map
  }, [creative.type, content])
  const captionLength = fieldLengthState(creative.type, 'caption', content)
  const [imagePickerOpen, setImagePickerOpen] = useState(false)
  const hasVideo = !!(content.videoAttachmentId || content.videoUrl || content.videoStatus)

  const update = useCallback(
    (field: keyof LinkedInPostContent, value: unknown) => {
      updateContent(creative.id, { [field]: value })
    },
    [creative.id, updateContent]
  )

  return (
    <div ref={editorRef} className="space-y-4" onBlurCapture={handleEditorBlur}>
      <FieldGroup label="Image" sectionId="image" highlighted={highlightedSection === 'image'}>
        <ImagePickerButton
          imageUrl={content.imageUrl}
          onClick={() => setImagePickerOpen(true)}
          emptyStateHint="Recommended: 1200×1200 or 1200×627"
        />
      </FieldGroup>

      {hasVideo && (
        <FieldGroup label="Video" sectionId="video" highlighted={highlightedSection === 'video'}>
          <VideoPickerBody
            creativeId={creative.id}
            creativeType={creative.type}
            platform="linkedin"
            selectedVideoAttachmentId={content.videoAttachmentId}
            selectedVideoUrl={content.videoUrl}
            onSelectVideo={(attachmentId, videoUrl, videoSource) => {
              updateContent(creative.id, { videoAttachmentId: attachmentId, videoUrl, videoSource })
              flushPersistNow(creative.id)
            }}
          />
        </FieldGroup>
      )}

      <FieldGroup
        label="Text"
        count={captionLength ? `${captionLength.count}/${captionLength.max}` : undefined}
        countOver={captionLength?.over}
        error={violationByField.get('caption')}
        sectionId="caption"
        highlighted={highlightedSection === 'caption'}
      >
        <textarea
          value={content.caption}
          onChange={e => update('caption', e.target.value)}
          onFocusCapture={() => setActiveSection(creative.id, 'caption')}
          placeholder="Write your post text..."
          rows={5}
          className="w-full resize-none rounded-lg border border-line-bright bg-bg-3 px-3 py-2 text-sm text-paper transition-colors outline-none placeholder:text-paper-faint focus:border-acid"
        />
      </FieldGroup>

      <FieldGroup
        label={`Hashtags (${content.hashtags.length})`}
        sectionId="hashtags"
        highlighted={highlightedSection === 'hashtags'}
      >
        <HashtagInput
          hashtags={content.hashtags}
          onChange={hashtags => update('hashtags', hashtags)}
          onFocusCapture={() => setActiveSection(creative.id, 'hashtags')}
        />
      </FieldGroup>

      <ImagePickerModal
        creativeId={creative.id}
        currentImageUrl={content.imageUrl}
        currentAttachmentId={content.imageAttachmentId}
        open={imagePickerOpen}
        onOpenChange={setImagePickerOpen}
        onSelect={({ imageUrl, imageAttachmentId }) => {
          updateContent(creative.id, { imageUrl, imageAttachmentId })
          setImagePickerOpen(false)
        }}
      />
    </div>
  )
}
